<script setup lang="ts">
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Select from 'primevue/select';
import { onMounted, ref, watch } from 'vue';
import TimeTrackingService from '../../services/TimeTrackingService';
import backendCall from '../../services/AxiosService';
import { useToast } from 'primevue/usetoast';

const props = defineProps<{
  buildId: number;
}>();

const emit = defineEmits(['refreshTimes', 'update:visible']);

const toast = useToast();

const visible = defineModel<boolean>('visible');
const loading = ref(false);
const loadingUsers = ref(false);

interface User {
  id: number;
  first_name: string;
  last_name: string;
}

const users = ref<User[]>([]);

const formData = ref<{
  userId: number | null;
  startTime: Date | null;
  endTime: Date | null;
}>({
  userId: null,
  startTime: null,
  endTime: null,
});

// Fetch users who can be assigned time records
const fetchUsers = async () => {
  try {
    loadingUsers.value = true;
    const response = await backendCall.get('admin/users/for-work-orders');
    users.value = response.data?.data?.users || [];
  } catch (error) {
    console.error('Error fetching users:', error);
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to load users', life: 3000 });
  } finally {
    loadingUsers.value = false;
  }
};

// Reset form when dialog opens
watch(visible, (newValue) => {
  if (newValue) {
    // Set default times: 1 hour ago to now
    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
    formData.value = {
      userId: null,
      startTime: oneHourAgo,
      endTime: now,
    };
    void fetchUsers();
  }
});

const submitTimeRecord = async () => {
  if (!formData.value.userId) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Please select a worker', life: 3000 });
    return;
  }

  if (!formData.value.startTime) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Start time is required', life: 3000 });
    return;
  }

  if (!formData.value.endTime) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'End time is required', life: 3000 });
    return;
  }

  if (formData.value.endTime <= formData.value.startTime) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'End time must be after start time', life: 3000 });
    return;
  }

  try {
    loading.value = true;
    console.log('Creating time record with:', {
      buildId: props.buildId,
      userId: formData.value.userId,
      startTime: formData.value.startTime?.toISOString(),
      endTime: formData.value.endTime?.toISOString()
    });

    const result = await TimeTrackingService.createManualBuildTimeRecord(
      props.buildId,
      formData.value.userId,
      formData.value.startTime.toISOString(),
      formData.value.endTime.toISOString()
    );

    console.log('Time record creation result:', result);

    toast.add({ severity: 'success', summary: 'Success', detail: 'Time record created', life: 3000 });
    emit('refreshTimes');
    visible.value = false;
  } catch (error: any) {
    console.error('Error creating time record:', error);
    console.error('Error response:', error.response);
    const message = error.response?.data?.error?.message || 'Error creating time record';
    toast.add({ severity: 'error', summary: 'Error', detail: message, life: 5000 });
  } finally {
    loading.value = false;
  }
};

const handleClose = () => {
  visible.value = false;
};

const formatUserName = (user: User) => {
  return `${user.first_name} ${user.last_name}`;
};

onMounted(() => {
  if (visible.value) {
    void fetchUsers();
  }
});
</script>

<template>
  <Dialog
    v-model:visible="visible"
    append-to="body"
    modal
    :breakpoints="{ '960px': '75vw', '640px': '96vw' }"
    :style="{ width: '40vw' }"
    :draggable="false"
    :resizable="false"
  >
    <template #header>
      <div class="flex flex-col gap-2">
        <h1 class="m-0 text-surface-900 dark:text-surface-0 font-semibold text-xl leading-normal">
          Add Time Record
        </h1>
      </div>
    </template>

    <form
      class="flex flex-col gap-4 mt-4"
      @submit.prevent="submitTimeRecord"
    >
      <div>
        <label
          for="worker"
          class="block mb-1 text-color text-base"
        >Worker</label>
        <InputGroup>
          <InputGroupAddon>
            <i class="pi pi-user" />
          </InputGroupAddon>
          <Select
            id="worker"
            v-model="formData.userId"
            :options="users"
            option-value="id"
            :option-label="formatUserName"
            placeholder="Select a worker"
            :loading="loadingUsers"
            filter
            class="w-full"
          >
            <template #option="{ option }">
              {{ option.first_name }} {{ option.last_name }}
            </template>
          </Select>
        </InputGroup>
      </div>

      <div class="flex gap-4">
        <div class="w-full">
          <label
            for="start-time"
            class="block mb-1 text-color text-base"
          >Start Time</label>
          <InputGroup>
            <InputGroupAddon>
              <i class="pi pi-clock" />
            </InputGroupAddon>
            <DatePicker
              id="start-time"
              v-model="formData.startTime"
              show-time
              hour-format="24"
              fluid
            />
          </InputGroup>
        </div>
        <div class="w-full">
          <label
            for="end-time"
            class="block mb-1 text-color text-base"
          >End Time</label>
          <InputGroup>
            <InputGroupAddon>
              <i class="pi pi-clock" />
            </InputGroupAddon>
            <DatePicker
              id="end-time"
              v-model="formData.endTime"
              show-time
              hour-format="24"
              fluid
            />
          </InputGroup>
        </div>
      </div>
    </form>

    <template #footer>
      <div class="w-full flex gap-4 justify-end border-t border-surface pt-8">
        <Button
          label="Cancel"
          text
          @click="handleClose"
        />
        <Button
          label="Create"
          rounded
          :loading="loading"
          @click="submitTimeRecord"
        />
      </div>
    </template>
  </Dialog>
</template>